import {
  ContactInformation,
  GroupAgreementsDetails,
} from '../entities/installation-reporting-mdu.entities';

export interface InfoCardRow {
  translationKey: string;
  value: string | boolean | undefined;
}

export type InfoCard = InfoCardRow[];

export const DISPLAY_KEYS = [
  'propertyAddress',
  'propertyDesignation',
  'contactName',
  'contactPhone',
  'contactEmail',
  'isResidentialNetwork',
  'networkType',
  'propertyConnection',
  'connectionType',
  'propertyNetwork',
] as const;

export type DisplayKey = (typeof DISPLAY_KEYS)[number];

const VALUE_KEYS = [
  'isResidentialNetwork',
  'networkType',
  'propertyConnection',
  'connectionType',
  'propertyNetwork',
] as const;
type ValueKey = (typeof VALUE_KEYS)[number];

const translationPrefix = 'ordersOverview.reportDetails.';

const LABEL_PREFIXES: Record<DisplayKey, string> = {
  propertyAddress: 'address',
  propertyDesignation: 'propertyDesignation',
  contactName: 'deliveryContact',
  contactPhone: 'phone',
  contactEmail: 'email',
  isResidentialNetwork: 'housingNetwork',
  networkType: 'netType',
  propertyConnection: 'propertyConnection',
  connectionType: 'connectionType',
  propertyNetwork: 'propertyNetwork',
};

const VALUE_PREFIXES: Record<ValueKey, string> = {
  isResidentialNetwork: '',
  networkType: 'networkType.',
  propertyConnection: 'constructionStatus.',
  connectionType: 'connectionType.',
  propertyNetwork: 'propertyNetwork.',
};

export function detailsToInfoCards(
  details: GroupAgreementsDetails | null,
): InfoCard[] {
  const rows: InfoCardRow[] = DISPLAY_KEYS.map((key) => makeRow(key, details));

  return splitIntoCards(rows);
}

function makeRow(key: DisplayKey, details: GroupAgreementsDetails | null) {
  const labelKey = translationPrefix + 'labels.' + LABEL_PREFIXES[key];
  let rawValue: string | boolean | undefined = undefined;

  if (details) {
    const contact: ContactInformation = details.contactInformation ?? {};
    switch (key) {
      case 'contactName':
        rawValue = joinParts(
          [contact.firstName ?? undefined, contact.lastName ?? undefined],
          ' ',
        );
        break;
      case 'contactPhone':
        rawValue = joinParts(
          [
            contact.phoneNumberMobile ?? undefined,
            contact.phoneNumberHome ?? undefined,
          ],
          ', ',
        );
        break;
      case 'contactEmail':
        rawValue = contact.email ?? undefined;
        break;
      default: {
        const field = details[key];
        rawValue = field === null ? undefined : (field as string | boolean);
        break;
      }
    }
  }
  let value: string | boolean | undefined = rawValue;
  if (rawValue != null && (VALUE_KEYS as readonly string[]).includes(key)) {
    const prefix = translationPrefix + VALUE_PREFIXES[key as ValueKey];
    value = prefix + String(rawValue);
  }

  return { translationKey: labelKey, value };
}

function joinParts(
  parts: (string | undefined)[],
  seperator: string,
): string | undefined {
  const filtered = parts.filter((part): part is string => !!part);
  return filtered.length ? filtered.join(seperator) : undefined;
}

function splitIntoCards(rows: InfoCardRow[]): InfoCard[] {
  const rowLength = rows.length;
  const cardSize = 5;
  const cards: InfoCard[] = [];

  for (let start = 0; start < rowLength; start += cardSize) {
    cards.push(rows.slice(start, start + cardSize));
  }
  return cards;
}
